import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FontSizes,
  IButtonStyles,
  mergeStyles,
  Modal,
  PrimaryButton,
  Stack,
  Text,
} from "@fluentui/react";
import { Project } from "../../models";
import Loader from "../../components/Loader";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  getPrecedentDocuments,
} from "../../modules/redux/Documents/document";
import {
  getAllProjectDetails,
  getAllProjects,
  getWorkingGroup,
} from "../../modules/redux/Projects/project";
import { getContacts } from "../../modules/redux/ContactsSlice/contacts";
import "./index.css";
import { ProjectDetailsCard } from "../../components/Cards/ProjectDetailsCard";
import { DocsInProject } from "./DocsInProject";
import FadeTransition from "../../components/FadeTransition";
import { HorizontalStack, VerticalStack } from "../../components/Stack";
import DragAndDropComp from "./dragAndDrop";
import { colorPallete } from "../../assets/color";
import Help from "../../components/Help";
import useModal from "../../hooks/useModal";
import CrossIcon from "../../assets/Icons/svg/crossIcon";
import SuccessIcon from "../../assets/Icons/svg/successIcon";

const DocumentPage: FC = () => {
  const { t } = useTranslation();
  let { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [toggleProjectModal, setToggleProjectModal] = useState(false);
  const showToggleProjectModal = () => setToggleProjectModal(true);
  const hideToggleProjectModal = () => setToggleProjectModal(false);
  const [active, setActive] = useState(null);

  const { isModalOpen, showModal, hideModal } = useModal();
  const [rootDocumentId, setRootDocumentId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([]);
  const [uploadedVersionRootDocIds, setUploadedVersionRootDocIds] = useState<number[]>([]);

  const handleUploadSuccess = (
    fileNames: string[],
    versionRootDocIds: number[] = []
  ) => {
    setUploadedFileNames(fileNames);
    setUploadedVersionRootDocIds(versionRootDocIds);
  };

  const projectState = useAppSelector(
    (state: { fetchProjectDetails: any }) => state.fetchProjectDetails
  );
  const [projectData, setProjectData] = useState<Project | null>(null);
  const [permsission, setPermission] = useState<boolean>(false);
  const [folderName, setFolderName] = useState("");
  const createdRef = useRef(location.state?.created ? "open" : null);
  const created = createdRef.current;
  const isRestoreModeRef = useRef(!!location.state?.restoreUploadModal);

  useEffect(() => {
    if (location.state?.created) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  useEffect(() => {
    if (location.state?.restoreUploadModal) {
      showModal();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location]);

  useEffect(() => {
    if (projectState.data && typeof projectState.data === 'object' && projectState.data.id) {
      setProjectData(projectState.data);
    }
  }, [projectState.data]);

  useEffect(() => {
    setFolderName(projectData?.name!);
  }, [projectData?.name]);


  useEffect(() => {
    if (projectId) {
      dispatch(getAllProjectDetails(parseInt(projectId, 10)));
      dispatch(
        getAllProjects({
          limit: 500,
          offset: 0,
        })
      );
      dispatch(getWorkingGroup(parseInt(projectId, 10)));
      dispatch(getContacts());
    }
  }, [projectId]);

  function refreshData() {
    projectId && dispatch(getAllProjectDetails(parseInt(projectId, 10)));
    dispatch(
      getPrecedentDocuments({
        limit: 30,
        offset: 0,
      })
    );
    dispatch(
      getAllProjects({
        limit: 30,
        offset: 0,
      })
    );
  }

  const modalStyles = {
    root: {
      background: "rgba(0, 0, 0, 0.5)",
    },
    main: {
      borderRadius: "12px",
      width: "752px",
      padding: "18px 8px 8px 8px",
      alignItems: "center",
      background: "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)",
    },
  };

  const Title = () => {
    return (
      <HorizontalStack style={styles.titleContainer}>
        <Text
          styles={{
            root: {
              color: "#323232",
              fontFamily: "Poppins",
              fontSize: "18px",
              fontWeight: 500,
              lineHeight: "26px",
            },
          }}
        >
          Success !
        </Text>
        <Stack
          onClick={() => {
            hideToggleProjectModal();
          }}
          style={{ cursor: "pointer" }}
        >
          <CrossIcon />
        </Stack>
      </HorizontalStack>
    );
  };

  const numericProjectId = projectId ? parseInt(projectId, 10) : 0;

  // DEX-573 M2: the loaded project details lag the URL `projectId` — on a switch
  // there is a window where `projectId` already points at project B but the store
  // (and the derived `projectData`) still hold project A. Detect that window and
  // gate on it so project A never paints while B loads (navigation.feature @m2).
  const dataIsString = typeof projectState.data === "string";
  const projectMatches = projectData?.id === numericProjectId;
  const projectSwitching =
    !!projectId &&
    !projectMatches &&
    projectState.error === "" &&
    !dataIsString;

  const projectErrorJSX = projectState.isLoading || projectSwitching ? (
    <Stack className={mergeStyles(styles.loader)}>
      <Stack style={{ margin: "2%" }}>
        <Loader />
      </Stack>
      <Text
        className={mergeStyles({
          fontSize: FontSizes.size32,
          fontFamily: "Poppins",
        })}
      >
        {t("loading")}
      </Text>
    </Stack>
  ) : !projectState.isLoading && projectState.error !== "" ? (
    <Stack className={mergeStyles(styles.loader)}>
      <Text className={mergeStyles(styles.textStyles)}>{t("loader")}</Text>
      <PrimaryButton
        styles={retryButtonStyles}
        onClick={() => dispatch(getAllProjectDetails(Number(projectId!)))}
      >
        {t("retry")}
      </PrimaryButton>
    </Stack>
  ) : null;

  return (
    <FadeTransition>
      {(permsission || isLoading) && !isRestoreModeRef.current ? (
        <Stack className={mergeStyles(styles.loader)}>
          <Stack style={{ margin: "2%" }}>
            <Loader />
          </Stack>
          <Text
            className={mergeStyles({
              fontSize: FontSizes.size32,
              fontFamily: "Poppins",
            })}
          >
            {t("loading")}
          </Text>
        </Stack>
      ) : projectErrorJSX && !isRestoreModeRef.current ? (
        projectErrorJSX
      ) : projectMatches || isRestoreModeRef.current ? (
        <>
          <VerticalStack style={{ ...styles.container, position: "relative" }}>
            {projectState.data?.name && (
              <>
                <ProjectDetailsCard
                  setIsLoading={setIsLoading}
                  projectData={projectData}
                  active={active}
                  setActive={setActive}
                  showToggleProjectModal={showToggleProjectModal}
                />
                <Modal
                  styles={modalStyles}
                  isOpen={toggleProjectModal}
                  onDismiss={hideToggleProjectModal}
                  isBlocking={true}
                >
                  <Title />
                  <Stack
                    style={{
                      flexDirection: "column",
                      background: "white",
                      padding: 20,
                      borderRadius: "8px",
                    }}
                  >
                    <SuccessIcon />
                    <Text
                      style={{
                        fontSize: "16px",
                        fontFamily: "Poppins",
                        paddingLeft: "5px",
                        paddingTop: "10px",
                      }}
                    >
                      Project status changed to {active ? "Active" : "Closed"}
                    </Text>
                  </Stack>
                </Modal>
              </>
            )}
            <div className="LightGreyCard">
              <DragAndDropComp
                showImportModal={showModal}
                hideImportModal={hideModal}
                isImportModalOpen={isModalOpen}
                created={created}
                projectId={projectData?.id ?? numericProjectId}
                refresh={refreshData}
                folderName={folderName}
                rootDocumentId={rootDocumentId}
                setRootDocumentId={setRootDocumentId}
                onUploadSuccess={handleUploadSuccess}
              />
              {projectState.data?.name && (
                <DocsInProject
                  projectData={projectData}
                  refresh={refreshData}
                  showImportModal={showModal}
                  hideImportModal={hideModal}
                  isImportModalOpen={isModalOpen}
                  setRootDocumentId={setRootDocumentId}
                  uploadedFileNames={uploadedFileNames}
                  onUploadHandled={() => setUploadedFileNames([])}
                  uploadedVersionRootDocIds={uploadedVersionRootDocIds}
                  onVersionUploadHandled={() => setUploadedVersionRootDocIds([])}
                />
              )}
            </div>
            <Help />
          </VerticalStack>
        </>
      ) : (
        <Stack
          style={{
            width: "100%",
            alignItems: "center",
            alignSelf: "center",
            marginTop: "10%",
            height: "380px",
            justifyContent: "center",
          }}
        >
          <Text
            style={{
              fontSize: "24px",
              lineHeight: "36px",
              fontWeight: 600,
              fontFamily: "Poppins",
              color: colorPallete.black1,
            }}
          >
            {typeof projectState.data === "string" && projectState.data}
          </Text>
        </Stack>
      )}
    </FadeTransition>
  );
};
export default DocumentPage;
const retryButtonStyles: IButtonStyles = {
  root: {
    width: "20px",
    margin: "1%",
    alignItems: "center",
  },
};
const styles = {
  container: {
    width: "100%",
    alignItems: "center",
    overflow: "hidden",
    marginTop: "1rem",
    backgroundColor: "",
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    alignItems: "center",
    paddingTop: "10%",
  },
  textStyles: {
    fontSize: FontSizes.size24,
    fontFamily: "Poppins",
  },
  titleContainer: {
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 14px 10px 16px",
  },
};
